import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AuthMark, AuthShell } from '@/components/auth/AuthShell';
import { INVALID_FIELD } from '@/components/auth/authStyles';
import { FieldError } from '@/components/auth/FieldError';
import { invalidFieldProps, revealInvalidField } from '@/components/auth/fieldValidation';
import { PasswordPolicyHint } from '@/components/auth/PasswordPolicyHint';
import { Button } from '@/components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, handleApiError } from '@/lib/api';
import { validatePassword } from '@/lib/password-policy';
import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
export default function ResetPassword({ token }) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    // Shown under the field as well as in the toast: on iOS/iPadOS the keyboard
    // stays up after "Go" and hides the bottom toast entirely.
    const [errors, setErrors] = useState({});
    const rejectWith = (next) => {
        setErrors(next);
        revealInvalidField(document.getElementById(next.password ? 'password' : 'confirmPassword'));
        toast({
            title: t('validation.error'),
            description: next.password ?? next.confirm,
            variant: 'destructive',
        });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password || !confirmPassword) {
            const required = t('validation.fillAllFields');
            rejectWith({
                password: password ? undefined : required,
                confirm: confirmPassword ? undefined : required,
            });
            return;
        }
        const passwordError = validatePassword(password);
        if (passwordError) {
            rejectWith({ password: passwordError });
            return;
        }
        if (password !== confirmPassword) {
            rejectWith({ confirm: t('validation.passwordsDoNotMatch') });
            return;
        }
        setErrors({});
        setIsSubmitting(true);
        try {
            await apiRequest('POST', `/auth/reset-password/${token}`, { password });
            setIsSuccess(true);
            toast({
                title: t('resetPassword.successTitle'),
                description: t('resetPassword.successDescription'),
            });
        }
        catch (error) {
            handleApiError(error);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    if (isSuccess) {
        return (_jsxs(AuthShell, { children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx("div", { className: "mb-4 flex justify-center", children: _jsx(CheckCircle2, { className: "text-primary h-16 w-16" }) }), _jsx(CardTitle, { className: "text-2xl font-bold", children: t('resetPassword.successTitle') }), _jsx(CardDescription, { children: t('resetPassword.successDetail') })] }), _jsx(CardContent, { children: _jsx(Link, { href: "/login", children: _jsx(Button, { className: "w-full", children: t('resetPassword.goToLogin') }) }) })] }));
    }
    return (_jsxs(AuthShell, { children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx(AuthMark, {}), _jsx(CardTitle, { className: "text-2xl font-bold", children: t('resetPassword.title') }), _jsx(CardDescription, { children: t('resetPassword.subtitle') })] }), _jsx(CardContent, { children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "password", children: t('resetPassword.newPassword') }), _jsx(PasswordInput, { id: "password", placeholder: t('resetPassword.passwordPlaceholder'), value: password, onChange: (e) => {
                                        setPassword(e.target.value);
                                        setErrors((prev) => ({ ...prev, password: undefined }));
                                    }, disabled: isSubmitting, autoComplete: "new-password", enterKeyHint: "next", className: INVALID_FIELD, ...invalidFieldProps('password-error', errors.password, password && 'password-rules') }), _jsx(FieldError, { id: "password-error", message: errors.password }), _jsx(PasswordPolicyHint, { id: "password-rules", password: password })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "confirmPassword", children: t('resetPassword.confirmPassword') }), _jsx(PasswordInput, { id: "confirmPassword", placeholder: t('resetPassword.confirmPlaceholder'), value: confirmPassword, onChange: (e) => {
                                        setConfirmPassword(e.target.value);
                                        setErrors((prev) => ({ ...prev, confirm: undefined }));
                                    }, disabled: isSubmitting, autoComplete: "new-password", enterKeyHint: "go", className: INVALID_FIELD, ...invalidFieldProps('confirm-password-error', errors.confirm) }), _jsx(FieldError, { id: "confirm-password-error", message: errors.confirm })] }), _jsx(Button, { type: "submit", className: "w-full", loading: isSubmitting, loadingText: t('resetPassword.submitting'), children: t('resetPassword.submit') })] }) })] }));
}
